import { ContentType, RAI_STATUS, ErrorResponse, URL_PERMISSIONS_MAPPING_TYPE } from "@/types";
import { RAIOutputCheckData, ModelOutputs } from "@/app/(protected)/concepting/components/StepFour/types";
import { IMAGE_GENERATION_RESTRICTED, HTTP_STATUS } from "@/constants/constants";
import { RAIResponse } from "@/app/(protected)/generate-lifestyle/components/StepTwo/components/InputList";

export type Category =
  | "Potentially Offensive Content"
  | "Human Likeness or Celebrity Likeness"
  | "Intellectual Property (Copyright, Trademark)"
  | "Manipulative Language"
  | "Overconsumption, Poor Eating Habits"
  | "Alcohol, Tobacco, Drug Use"
  | "Profanity"
  | "Eating Check-in"
  | "Weapons or acts of violence";

export type CategoryMapping = {
  [key in
    | "RAI001"
    | "RAI002"
    | "RAI003"
    | "RAI004"
    | "RAI005"
    | "RAI006"
    | "RAI007"
    | "RAI008"
    | "RAI009"
    | "RAI010"
    | "RAI011"
    | "RAI012"
    | "RAI013"
    | "RAI014"
    | "RAI015"
    | "RAI016"
    | "RAI017"
    | "RAI018"
    | "RAI019"
    | "RAI020"
    | "RAI021"
    | "RAI022"
    | "RAI023"
    | "RAI024"
    | "RAI025"
    | "RAI026"
    | "RAI027"
    | "RAI028"
    | "RAI029"]: Category;
};

export const raiCategoryMapping: CategoryMapping = {
  RAI001: "Potentially Offensive Content",
  RAI002: "Potentially Offensive Content",
  RAI003: "Potentially Offensive Content",
  RAI004: "Human Likeness or Celebrity Likeness",
  RAI005: "Human Likeness or Celebrity Likeness",
  RAI006: "Intellectual Property (Copyright, Trademark)",
  RAI007: "Intellectual Property (Copyright, Trademark)",
  RAI008: "Manipulative Language",
  RAI009: "Human Likeness or Celebrity Likeness",
  RAI010: "Overconsumption, Poor Eating Habits",
  RAI011: "Overconsumption, Poor Eating Habits",
  RAI012: "Overconsumption, Poor Eating Habits",
  RAI013: "Overconsumption, Poor Eating Habits",
  RAI014: "Alcohol, Tobacco, Drug Use",
  RAI015: "Alcohol, Tobacco, Drug Use",
  RAI016: "Profanity",
  RAI017: "Eating Check-in",
  RAI018: "Weapons or acts of violence",
  RAI019: "Weapons or acts of violence",
  RAI020: "Potentially Offensive Content",
  RAI021: "Potentially Offensive Content",
  RAI022: "Human Likeness or Celebrity Likeness",
  RAI023: "Intellectual Property (Copyright, Trademark)",
  RAI024: "Human Likeness or Celebrity Likeness",
  RAI025: "Overconsumption, Poor Eating Habits",
  RAI026: "Overconsumption, Poor Eating Habits",
  RAI027: "Alcohol, Tobacco, Drug Use",
  RAI028: "Eating Check-in",
  RAI029: "Weapons or acts of violence",
};

export const getRaiMessage = (inputValue: string, raiMessages?: RAIResponse[]) => {
  const message = raiMessages?.find((m) => m.assetText === inputValue);
  if (!message) return;
  const isErrorMessage = message?.complianceIds && message?.complianceIds?.length > 0;
  const successMessage = message?.successMessage;
  const isNotChanged = message.assetText === inputValue;
  const raiErrorMessage = isErrorMessage ? message?.errorMessage : [];
  const failedCompliances = message.failedCompliances;
  return { successMessage, raiErrorMessage, isErrorMessage, isNotChanged, failedCompliances };
};

export const getIsModalRAISuccess = (raiData: RAIOutputCheckData, type: ContentType) => {
  if (!type?.value) return false;
  const key = type.value === "images" ? "image" : type.value;

  if (!(key in raiData)) return false;

  const data = raiData[key as keyof RAIOutputCheckData];

  // Handle array type for images
  if (Array.isArray(data)) {
    // For array of images, check if all have PASS status
    return data.every((item) => item.status === RAI_STATUS.PASS);
  }

  // Handle object type for other fields
  return data?.status === RAI_STATUS.PASS;
};

export const getHeaders = (projectId?: string) => {
  return {
    "Content-Type": "application/json",
    ...(projectId && { "X-Project-Id": projectId }),
  };
};

export const getErrorType = (error: ErrorResponse) => {
  const errorState = {
    showTimeoutError: false,
    showErrorPage: false,
    showImageGenerationError: false,
  };
  if (!error?.success) {
    const responseStatus = error?.details?.response?.status;
    if (responseStatus >= HTTP_STATUS.BAD_REQUEST && error?.details?.response?.data?.code === IMAGE_GENERATION_RESTRICTED) {
      errorState.showImageGenerationError = true;
    } else if (responseStatus >= HTTP_STATUS.BAD_REQUEST && responseStatus < HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      errorState.showErrorPage = true;
    } else {
      errorState.showTimeoutError = true;
    }
  }
  return errorState;
};

export const removeEntryFromMap = (map: Map<string, ModelOutputs>, entryKey: string) => {
  if (map.has(entryKey)) {
    map.delete(entryKey);
  }
};

export const getMatchedRoute = (value: string, map: URL_PERMISSIONS_MAPPING_TYPE): string =>
  Object.keys(map).find((key) => value.includes(key)) ?? "";

export function hasKeys(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj) && Object.keys(obj).length > 0;
}

export function isEmptyObject(obj: unknown): boolean {
  return !hasKeys(obj);
}
